import {
  pickInitialMediaSource,
  selectMediaSource
} from "../core/math.js";

function wait(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function waitForCanPlay(video, timeoutMs = 12000) {
  if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("Video loading timed out."));
    }, timeoutMs);
    const cleanup = () => {
      window.clearTimeout(timeout);
      video.removeEventListener("canplay", onReady);
      video.removeEventListener("error", onError);
    };
    const onReady = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(video.error ?? new Error("Video failed to load."));
    };
    video.addEventListener("canplay", onReady, { once: true });
    video.addEventListener("error", onError, { once: true });
  });
}

export class VideoController {
  #video;
  #store;
  #config;
  #gate;
  #mist;
  #selectedSource = null;
  #targetSource = null;
  #upgradeTimer = 0;
  #looping = false;
  #destroyed = false;
  #visibilityHandler;
  #gateHandler;
  #offLoadingComplete = null;

  constructor({ video, store, config, gate, mist }) {
    this.#video = video;
    this.#store = store;
    this.#config = config;
    this.#gate = gate;
    this.#mist = mist;

    this.#visibilityHandler = () => {
      if (document.hidden) {
        this.#video.pause();
      } else if (this.#store.get().lifecycle === "ready") {
        this.play().catch(() => this.#showGate());
      }
    };
    document.addEventListener("visibilitychange", this.#visibilityHandler);

    this.#gateHandler = () => {
      this.play()
        .then(() => {
          this.#gate.hidden = true;
        })
        .catch(() => {
          this.#store.emit("media:error", {
            message: "浏览器仍阻止了背景播放。"
          });
        });
    };
    this.#gate.addEventListener("click", this.#gateHandler);
  }

  get element() {
    return this.#video;
  }

  get currentSource() {
    return this.#selectedSource;
  }

  async init(tier) {
    if (tier === "reduced") {
      this.#store.patch(
        {
          lifecycle: "static-fallback",
          performance: { tier },
          media: { playing: false }
        },
        "media:static"
      );
      document.body.classList.add("is-static");
      return false;
    }

    const connection = navigator.connection ?? {};
    this.#targetSource = selectMediaSource({
      sources: this.#config.sources,
      viewportWidth: window.innerWidth,
      devicePixelRatio: window.devicePixelRatio,
      tier,
      saveData: Boolean(connection.saveData),
      effectiveType: connection.effectiveType ?? "4g"
    });
    const initialSource = pickInitialMediaSource(
      this.#targetSource,
      this.#config.sources
    );

    try {
      await this.setSource(initialSource, { preserveTime: false });
      await this.play();
      this.#store.patch(
        {
          lifecycle: "ready",
          media: {
            source: initialSource.id,
            duration: this.#video.duration || this.#config.duration,
            playing: true
          }
        },
        "media:ready"
      );

      if (initialSource.id !== this.#targetSource.id) {
        this.#scheduleUpgrade();
      }
      return true;
    } catch (error) {
      this.#store.patch(
        {
          lifecycle: "static-fallback",
          media: { playing: false }
        },
        "media:error"
      );
      this.#store.emit("media:error", { error });
      document.body.classList.add("is-static");
      return false;
    }
  }

  async setSource(source, { preserveTime = true } = {}) {
    if (!source || this.#destroyed || this.#selectedSource?.id === source.id) {
      return;
    }

    const previousSource = this.#selectedSource;
    const previousTime = preserveTime ? this.#video.currentTime : 0;
    const wasPlaying = !this.#video.paused;
    this.#video.src = source.src;
    this.#video.load();

    try {
      await waitForCanPlay(this.#video);
      if (preserveTime && Number.isFinite(previousTime)) {
        this.#video.currentTime = Math.min(
          previousTime,
          Math.max(0, this.#video.duration - 0.2)
        );
      }
      this.#video.playbackRate = this.#config.playbackRate;
      this.#selectedSource = source;
      this.#store.patch(
        { media: { source: source.id } },
        "media:source"
      );
      if (wasPlaying || previousSource === null) {
        await this.play();
      }
    } catch (error) {
      if (previousSource && previousSource.src !== source.src) {
        this.#video.src = previousSource.src;
        this.#video.load();
        await waitForCanPlay(this.#video).catch(() => {});
        this.#video.currentTime = previousTime;
        this.#video.playbackRate = this.#config.playbackRate;
        if (wasPlaying) {
          await this.play().catch(() => {});
        }
      }
      throw error;
    }
  }

  #scheduleUpgrade() {
    const upgrade = () => {
      this.#upgradeTimer = window.setTimeout(() => {
        this.setSource(this.#targetSource, { preserveTime: true }).catch(
          (error) => {
            this.#store.emit("media:upgrade-failed", { error });
          }
        );
      }, 4500);
    };

    if (document.body.classList.contains("is-ready")) {
      upgrade();
      return;
    }

    this.#offLoadingComplete = this.#store.on(
      "loading:complete",
      () => {
        this.#offLoadingComplete?.();
        this.#offLoadingComplete = null;
        upgrade();
      },
      { once: true }
    );
  }

  async play() {
    if (!this.#store.get().audio.enabled) {
      this.#video.muted = true;
    }
    this.#video.playbackRate = this.#config.playbackRate;
    await this.#video.play();
    this.#store.patch({ media: { playing: true } }, "media:play");
  }

  #showGate() {
    this.#gate.hidden = false;
  }

  async #performLoop() {
    if (this.#looping || this.#destroyed) {
      return;
    }
    this.#looping = true;
    this.#mist.classList.add("is-looping");
    this.#store.emit("media:loop-start");
    await wait(420);
    this.#video.currentTime = 0.05;
    this.#video.playbackRate = this.#config.playbackRate;
    await this.#video.play().catch(() => this.#showGate());
    const loopIndex = this.#store.get().media.loopIndex + 1;
    this.#store.patch(
      {
        media: {
          loopIndex,
          currentTime: this.#video.currentTime,
          playing: !this.#video.paused
        }
      },
      "media:loop"
    );
    await wait(470);
    this.#mist.classList.remove("is-looping");
    this.#looping = false;
  }

  update() {
    if (
      this.#destroyed ||
      this.#video.readyState < HTMLMediaElement.HAVE_METADATA
    ) {
      return;
    }
    const currentTime = this.#video.currentTime;
    this.#store.patch(
      {
        media: {
          currentTime,
          duration: this.#video.duration || this.#config.duration,
          playing: !this.#video.paused
        }
      },
      "media:tick"
    );

    const duration = this.#video.duration || this.#config.duration;
    if (
      !this.#looping &&
      currentTime >= duration - this.#config.loopFadeLead
    ) {
      this.#performLoop();
    }
  }

  setPlaybackRate(value) {
    this.#video.playbackRate = value;
  }

  async setCurrentTime(value) {
    if (this.#video.readyState < HTMLMediaElement.HAVE_METADATA) {
      return;
    }
    this.#video.currentTime = Math.max(
      0,
      Math.min(value, this.#video.duration - 0.1)
    );
    await new Promise((resolve) => {
      this.#video.addEventListener("seeked", resolve, { once: true });
    });
  }

  destroy() {
    this.#destroyed = true;
    window.clearTimeout(this.#upgradeTimer);
    this.#offLoadingComplete?.();
    document.removeEventListener(
      "visibilitychange",
      this.#visibilityHandler
    );
    this.#gate.removeEventListener("click", this.#gateHandler);
    this.#video.pause();
    this.#video.removeAttribute("src");
    this.#video.load();
  }
}
